import { GUARDIAN_STATUS_LABEL } from './watch-format.js';
import { guardianTone } from './people-rows.js';

const lower = (s) => String(s ?? '').trim().toLowerCase();

export const OVERRIDES_OWN_UNSUBSCRIBE = new Set(['classic', 'owners']);
export function cannotClearText(scope) {
  if (scope === 'guardians') return 'Re-add the guardian to clear';
  if (scope === 'complained') return 'The recipient reported our email as spam. Only the recipient can undo it, so it cannot be cleared here.';
  return 'Cannot be cleared here';
}

function finish(r) {
  const g = r.guardian;
  let status = { key: 'active', label: 'Active', tone: 'green' };
  if (g && g.status === 'pending') status = { key: 'checking', label: 'Checking', tone: 'amber' };
  if (g && g.status !== 'valid' && g.status !== 'pending') status = { key: 'attention', label: GUARDIAN_STATUS_LABEL[g.status] ?? g.status, tone: 'red' };
  if (g?.undeliverable) status = { key: 'attention', label: 'Address bounced', tone: 'red' };
  if (r.suppressions.length > 0) status = { key: 'unsubscribed', label: 'Unsubscribed', tone: 'amber' };
  return { ...r, sources: [...r.sources], status };
}

/**
 * One row per address. `guardians` is the Watch list (null when the caller may not read Watch content),
 * `classicSuppressions` the daily-email unsubscribe list, `watchSuppressions` the Watch suppressions.
 */
const usable = (a) => typeof a === 'string' && a.trim() !== '';

export function buildRecipientRows({ rules = [], ruleLabel = (id) => id, guardians = null, classicSuppressions = [], watchSuppressions = null, alertEmail = '' } = {}) {
  const rows = new Map();
  const row = (address) => {
    const key = lower(address);
    if (!rows.has(key)) rows.set(key, { id: `addr:${key}`, kind: 'address', address, name: null, sources: new Set(), receives: [], guardian: null, suppressions: [], isDefault: false });
    return rows.get(key);
  };
  const addReceives = (r, chip) => { if (!r.receives.some((c) => c.id === chip.id)) r.receives.push(chip); };

  for (const g of guardians ?? []) {
    if (!usable(g.address ?? g.lookup)) continue;
    const r = row(g.address ?? g.lookup);
    r.sources.add('live');
    r.name = g.displayName ?? r.name;
    r.guardian = g;
    addReceives(r, { id: 'guardian', label: 'Guardian', tone: guardianTone(g.status), title: GUARDIAN_STATUS_LABEL[g.status] ?? g.status });
  }

  const owners = [];
  for (const rule of rules) {
    const label = ruleLabel(rule.eventId);
    for (const rec of rule.recipients ?? []) {
      if (rec.type === 'group-owners') { if (!owners.some((c) => c.id === `rule:${rule.ruleId}`)) owners.push({ id: `rule:${rule.ruleId}`, label, tone: 'slate' }); continue; }
      const addresses = rec.type === 'fixed' || rec.type === 'fallback' ? [rec.email] : rec.type === 'people' ? rec.emails ?? [] : rec.type === 'default' ? (usable(alertEmail) ? [alertEmail] : []) : [];
      for (const a of addresses.filter(usable)) {
        const r = row(a);
        r.sources.add('daily');
        addReceives(r, { id: `rule:${rule.ruleId}`, label, tone: 'slate' });
      }
    }
  }

  if (usable(alertEmail)) {
    const r = row(alertEmail);
    r.sources.add('daily');
    r.isDefault = true;
    addReceives(r, { id: 'default', label: 'Default alert address', tone: 'slate' });
  }

  for (const s of classicSuppressions) {
    if (!usable(s.email)) continue;
    const r = row(s.email);
    r.sources.add('daily');
    r.suppressions.push({ scope: 'classic', clearable: true, at: s.unsubscribedAt ?? null, classicList: true, address: s.email });
  }
  for (const s of watchSuppressions ?? []) {
    if (!usable(s.address)) continue;
    const r = row(s.address);
    r.sources.add('live');
    r.suppressions.push({ scope: s.scope, clearable: s.clearable === true, at: s.createdAt ?? null, classicList: false, address: s.address });
  }

  const out = [...rows.values()].map(finish);
  if (owners.length > 0) {
    out.push({ id: 'owners', kind: 'owners', address: null, name: 'Group owners', sources: ['daily'], receives: owners, guardian: null, suppressions: [], isDefault: false, status: { key: 'active', label: 'Resolved when a rule fires', tone: 'slate' } });
  }
  return out;
}

export const recipientLabel = (r) => r.address ?? r.name;
export const recipientSearchText = (r) => [r.name, r.address, ...r.receives.map((c) => c.label), r.status.label].filter(Boolean).join(' ');
